import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, BarChart3, Menu, X } from 'lucide-react';

const Header = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [marketData, setMarketData] = useState({
    marketCap: '0',
    marketRank: '0',
    isLoading: true,
    error: null
  });

  const navItems = [
    { name: 'About', section: 'about', path: '/' },
    { name: 'MemeFi', section: 'memefi', path: '/memefi' },
    { name: 'Boop Mini Games', section: 'games', path: '/games' },
    { name: 'Buy $BOOP', section: 'buy', path: '/buy', highlight: true }
  ];

  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        const response = await fetch('https://api.coingecko.com/api/v3/coins/boop-2?localization=false&tickers=false&market_data=true&community_data=false&developer_data=false&sparkline=false');
        
        if (!response.ok) {
          throw new Error('Failed to fetch market data');
        }

        const data = await response.json();
        
        setMarketData({
          marketCap: formatMarketCap(data.market_data.market_cap.usd),
          marketRank: data.market_cap_rank || 'N/A',
          isLoading: false,
          error: null
        });
      } catch (error) {
        setMarketData(prev => ({
          ...prev,
          isLoading: false,
          error: error.message
        }));
      }
    };

    fetchMarketData();
    const interval = setInterval(fetchMarketData, 300000);
    return () => clearInterval(interval);
  }, []);

  const formatMarketCap = (value) => {
    if (!value) return '0';
    if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
    return `$${value.toFixed(1)}`;
  };

  const handleNavigation = (path) => {
    if (path.startsWith('/#')) {
      const element = document.getElementById(path.substring(2));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(path);
    }
    setIsMenuOpen(false);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 bg-gray-900 border-b border-gray-800 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="h-16 flex items-center justify-between gap-4">
            {/* Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-gray-400 hover:text-white"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-3">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.path)}
                  className={`flex items-center px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors
                    ${item.highlight ? 
                      'bg-sky-500 hover:bg-sky-600 text-white' : 
                      'bg-gray-800 text-gray-400 hover:bg-gray-600 hover:text-white'}`}
                >
                  {item.name}
                </button>
              ))}
            </nav>

            {/* Market Data - Always Visible */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-gray-400">
                <BarChart3 className="w-4 h-4" />
                <span className="text-sm">
                  {marketData.isLoading ? 'Loading...' : 
                   marketData.error ? 'Error loading data' : 
                   `MCap: ${marketData.marketCap}`}
                </span>
              </div>

              <div className="flex items-center gap-2 text-gray-400">
                <Trophy className="w-4 h-4" />
                <span className="text-sm">
                  {marketData.isLoading ? 'Loading...' : 
                   marketData.error ? 'Error loading data' : 
                   `Rank: #${marketData.marketRank}`}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-gray-900 border-t border-gray-800">
            <div className="max-w-7xl mx-auto px-4 py-2">
              <nav className="flex flex-col gap-2">
                {navItems.map((item) => (
                  <button
                    key={item.name}
                    onClick={() => handleNavigation(item.path)}
                    className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors w-full justify-start
                      ${item.highlight ? 
                        'bg-sky-500 hover:bg-sky-600 text-white' : 
                        'bg-gray-800 text-gray-400 hover:bg-gray-600 hover:text-white'}`}
                  >
                    {item.name}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        )}
      </header>
      {/* Spacer */}
      <div className={`h-16 ${isMenuOpen ? 'md:h-16' : 'h-16'}`} />
    </>
  );
};

export default Header;